import { useVault } from '@apideck/vault-react';
import { PaidOnlyTag } from '@components';
import { useDemoGuard } from '@hooks';
import { AccountingService } from '@services';
import { Space } from 'antd';
import { useEffect, useState } from 'react';
import { SuccessButton } from './SuccessButton';

export interface VaultButtonProps {
  tenantId: number;
}

// Maps each apideck service_id to the settings key that holds the provider's tenant/org identifier.
// To support a new provider: add one entry here — nothing else needs to change.
const ACCOUNTING_TENANT_ID_KEYS: Record<string, string> = {
  xero: 'tenant_id',
  'sage-business-cloud-accounting': 'business_id',
  quickbooks: 'realm_id',
};

const resolveAccountingTenantId = (connection: any): string | undefined => {
  const key = ACCOUNTING_TENANT_ID_KEYS[connection.service_id];
  return key ? (connection.settings?.[key] ?? undefined) : undefined;
};

const resolveIsConfigured = (connection: any): boolean => {
  const key = ACCOUNTING_TENANT_ID_KEYS[connection.service_id];
  return connection.state === 'callable' && !!key && !!connection.settings?.[key];
};

// plenty of things can be done different here, but who knows when Kev will change his mind
// leave it like this for now

export const VaultButton: React.FC<VaultButtonProps> = ({ tenantId }) => {
  const apiEndpoint = 'accounting';
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [connectionState, setConnectionState] = useState<string | null>(null);
  const [apiDeckConnection, setApiDeckConnection] = useState<any>();

  const { open } = useVault();
  const { guard } = useDemoGuard();

  // thank you apideck for not exporting types, so i can type "any" you piece of shit
  const handleConnectionDelete = async (connection: any) => {
    await AccountingService.disconnectAccounting({
      tenantId,
      service: connection.service_id,
      friendlyName: connection.name,
      isConfigured: false,
      authorizeUrl: connection.authorize_url,
      revokeUrl: connection.revoke_url,
      accountingTenantId: undefined,
    });
  };

  useEffect(() => {
    if (connectionState && connectionState === 'added') {
      (async () => {
        await AccountingService.connectAccounting({
          tenantId,
          service: apiDeckConnection.service_id,
          friendlyName: apiDeckConnection.name,
          isConfigured: false,
          authorizeUrl: apiDeckConnection.authorize_url,
          revokeUrl: apiDeckConnection.revoke_url,
          accountingTenantId: undefined,
        });
      })();
    }
  }, [connectionState]);

  useEffect(() => {
    if (isAuthorized) {
      (async () => {
        debugger;
        await AccountingService.connectAccounting({
          tenantId,
          service: apiDeckConnection.service_id,
          friendlyName: apiDeckConnection.name,
          isConfigured,
          authorizeUrl: apiDeckConnection.authorize_url,
          revokeUrl: apiDeckConnection.revoke_url,
          accountingTenantId: resolveAccountingTenantId(apiDeckConnection),
        });
      })();
    }
  }, [isAuthorized, isConfigured]);

  const handleConnectionChange = async (connection: any) => {
    const isAuthorized = connection.state === 'authorized' || connection.state === 'callable';
    // we will see for other providers how this works, for xero must be 'callable'

    setIsAuthorized(isAuthorized);
    setIsConfigured(resolveIsConfigured(connection));

    setApiDeckConnection(connection);
    setConnectionState(connection.state);
  };

  // Guarded at the entry point rather than inside createSession, so a demo tenant never
  // reaches the Apideck vault UI at all — connecting real accounting credentials is not
  // something a trial should be part-way through.
  const openVault = guard(() => {
    if (token)
      open({
        token: token,
        unifiedApi: apiEndpoint,
        showAttribution: false,
        onConnectionChange: handleConnectionChange,
        onConnectionDelete: handleConnectionDelete,
      });
    else createSession();
  });

  const createSession = async () => {
    setIsCreatingSession(true);
    try {
      const session = await AccountingService.createSession();

      if (session.token) {
        open({
          token: session.token,
          unifiedApi: apiEndpoint,
          showAttribution: false,
          onConnectionChange: handleConnectionChange,
          onConnectionDelete: handleConnectionDelete,
        });
        setToken(session.token);
      }
    } catch (error) {
      console.error('Error creating session:', error);
    } finally {
      setIsCreatingSession(false);
    }
  };

  // hide it temp, so we can access this shit
  return (
    <SuccessButton onClick={openVault} loading={isCreatingSession}>
      <Space size={8}>
        Manage accounting
        <PaidOnlyTag />
      </Space>
    </SuccessButton>
  );
};
